import { prefersReducedMotion } from '@theme/utilities';

/** @type {AudioContext | undefined} */
let audioCtx;

/**
 * Soft high-pass noise burst — perfume spritz.
 */
function playSpraySound() {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return;

  if (!audioCtx) {
    audioCtx = new AudioContextClass();
  }
  if (audioCtx.state === 'suspended') audioCtx.resume();

  const duration = 0.32;
  const sampleRate = audioCtx.sampleRate;
  const bufferSize = Math.floor(sampleRate * duration);
  const buffer = audioCtx.createBuffer(1, bufferSize, sampleRate);
  const data = buffer.getChannelData(0);

  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }

  const noise = audioCtx.createBufferSource();
  noise.buffer = buffer;

  const bandpass = audioCtx.createBiquadFilter();
  bandpass.type = 'highpass';
  bandpass.frequency.value = 3500;

  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.0001, audioCtx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.05, audioCtx.currentTime + 0.03);
  gain.gain.exponentialRampToValueAtTime(0.035, audioCtx.currentTime + 0.18);
  gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + duration);

  noise.connect(bandpass);
  bandpass.connect(gain);
  gain.connect(audioCtx.destination);

  noise.start();
  noise.stop(audioCtx.currentTime + duration);
}

/**
 * Perfume-style spray mist on collection cards.
 * Narrow upward-right cone, staggered over ~320ms like a real atomizer.
 *
 * @extends {HTMLElement}
 */
class CollectionCardSpray extends HTMLElement {
  /** @type {HTMLElement | null} */
  #card = null;

  /** @type {number} */
  #lastSpray = 0;

  /** @type {ReturnType<typeof setInterval> | null} */
  #spawner = null;

  /** Cooldown so hover + click don't double-fire */
  static COOLDOWN_MS = 600;

  connectedCallback() {
    this.#card = this.closest('.collection-card');
    if (!this.#card) return;

    this.#card.addEventListener('pointerenter', this.#onInteract);
    this.#card.addEventListener('pointerdown', this.#onInteract);
  }

  disconnectedCallback() {
    this.#card?.removeEventListener('pointerenter', this.#onInteract);
    this.#card?.removeEventListener('pointerdown', this.#onInteract);
    if (this.#spawner) clearInterval(this.#spawner);
    this.#card = null;
  }

  /** @param {PointerEvent} event */
  #onInteract = (event) => {
    // Skip mouse-only hover duplicates: pointerdown already covers tap/click
    if (event.type === 'pointerenter' && event.pointerType === 'touch') return;
    this.spray();
  };

  #emitParticle() {
    // Narrow cone: up and slightly right (0° = right, -90° = straight up)
    const baseAngle = -50;
    const coneSpread = 22;
    const angle = (baseAngle + (Math.random() * 2 - 1) * coneSpread) * (Math.PI / 180);

    const m = document.createElement('div');
    m.className = 'collection-card__mist';
    const mDist = 26 + Math.random() * 36;
    const mDx = Math.cos(angle) * mDist;
    const mDy = Math.sin(angle) * mDist + 6;
    m.style.setProperty('--dx', `${mDx}px`);
    m.style.setProperty('--dy', `${mDy}px`);
    m.style.left = `${Math.random() * 4 - 2}px`;
    m.style.top = `${Math.random() * 4 - 2}px`;
    this.appendChild(m);
    requestAnimationFrame(() => m.classList.add('go'));
    m.addEventListener('animationend', () => m.remove(), { once: true });

    if (Math.random() < 0.7) {
      const d = document.createElement('div');
      d.className = 'collection-card__droplet';
      const dDist = 40 + Math.random() * 40;
      const dDx = Math.cos(angle) * dDist;
      const dDy = Math.sin(angle) * dDist + 8;
      d.style.setProperty('--dx', `${dDx}px`);
      d.style.setProperty('--dy', `${dDy}px`);
      d.style.left = `${Math.random() * 4 - 2}px`;
      d.style.top = `${Math.random() * 4 - 2}px`;
      this.appendChild(d);
      requestAnimationFrame(() => d.classList.add('go'));
      d.addEventListener('animationend', () => d.remove(), { once: true });
    }
  }

  spray() {
    if (prefersReducedMotion()) return;

    const now = performance.now();
    if (now - this.#lastSpray < CollectionCardSpray.COOLDOWN_MS) return;
    this.#lastSpray = now;

    if (this.#spawner) clearInterval(this.#spawner);

    playSpraySound();

    const sprayDurationMs = 320;
    const tickMs = 22;
    let elapsed = 0;

    this.#spawner = setInterval(() => {
      this.#emitParticle();
      this.#emitParticle();
      elapsed += tickMs;
      if (elapsed >= sprayDurationMs) {
        if (this.#spawner) clearInterval(this.#spawner);
        this.#spawner = null;
      }
    }, tickMs);
  }
}

if (!customElements.get('collection-card-spray')) {
  customElements.define('collection-card-spray', CollectionCardSpray);
}
